import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { trackEvent } from 'src/api/db/apiAnalytics'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { fetchOrgUserRow, orgUserQueryKey } from 'src/api/db/orgUserQuery'
import { queryKeys } from 'src/api/db/queryKeys'
import { useSupabaseClient } from 'src/api/supabase'
import { cleanUrl } from 'src/shared/utils/helpers'
import { buildLoginSignupPath, parseNextPath, paths } from 'src/routes/paths'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DEBOUNCE_DELAY, type BusinessOAuthProvider } from './consts'
import { AuthenticateFormData } from './types'
import {
  extractDomainFromEmail,
  extractRootDomainName,
  generateBrandfetchUrls,
  isEmailAlreadyRegisteredError,
  isIdentityAlreadyLinkedError,
  isWorkEmail,
  namesFromAuthMetadata,
} from './utils'

export const useEmailDebounce = (email: string) => {
  const [debouncedCompanyUrl, setDebouncedCompanyUrl] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      if (email && isWorkEmail(email)) {
        const domain = extractDomainFromEmail(email)
        setDebouncedCompanyUrl(domain ? cleanUrl(domain) : '')
      } else {
        setDebouncedCompanyUrl('')
      }
    }, DEBOUNCE_DELAY)

    return () => clearTimeout(timer)
  }, [email])

  return debouncedCompanyUrl
}

export type UseLoginSignupSubmitOptions = {
  /** When set, called instead of navigating to /signup-success after signup step 1 */
  onSignupOrgReady?: (orgId: string) => void | Promise<void>
  /** After magic-link or signup, land here instead of /app */
  nextPath?: string
  /** Join this org instead of creating or renaming one during signup */
  inviteOrganizationId?: string
}

const OAUTH_PROVISION_LOCK_PREFIX = 'oauth-org-provisioned:'

const oauthProvisionLockKey = (userId: string) =>
  `${OAUTH_PROVISION_LOCK_PREFIX}${userId}`

const clearOAuthProvisionLocks = () => {
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith(OAUTH_PROVISION_LOCK_PREFIX)) {
      sessionStorage.removeItem(key)
    }
  }
}

export const useLoginSignupSubmit = (
  orgId: string | null,
  setOrgId: (id: string) => void,
  isLoginMode: boolean,
  options?: UseLoginSignupSubmitOptions
) => {
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [sentToEmail, setSentToEmail] = useState('')
  const { t } = useLocalization()
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { session, loading: authLoading } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const oauthReturnHandled = useRef(false)

  const resetMagicLinkState = useCallback(() => {
    setMagicLinkSent(false)
    setSentToEmail('')
  }, [])

  const { orgUser, updateUser } = useOrgUsersApi()
  const { organization, updateOrganization, createOrganization2 } = useOrgsApi({
    org_id: orgId || undefined,
  })
  const nextPath =
    parseNextPath(options?.nextPath) ?? paths.myAccount
  const inviteOrganizationId = options?.inviteOrganizationId
  const isOAuthReturn = searchParams.get('oauth') !== null

  const showError = (title: string, description: string) => {
    toast({ title, description, variant: 'destructive' })
  }

  const clearOAuthSearchParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('oauth')
        next.delete('error')
        next.delete('error_description')
        next.delete('error_code')
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  const goToSignupSuccess = async (newOrgId: string) => {
    setOrgId(newOrgId)
    if (options?.onSignupOrgReady) {
      await options.onSignupOrgReady(newOrgId)
      return
    }
    navigate(
      nextPath === paths.myAccount
        ? `${paths.signupSuccess}?org_id=${newOrgId}`
        : `${paths.signupSuccess}?org_id=${newOrgId}&next=${encodeURIComponent(nextPath)}`
    )
  }

  const startOAuth = async (provider: BusinessOAuthProvider) => {
    try {
      setOauthLoading(true)
      clearOAuthProvisionLocks()
      const redirectTo = inviteOrganizationId
        ? (() => {
            const url = new URL(window.location.href)
            url.searchParams.set('oauth', '1')
            return url.toString()
          })()
        : `${window.location.origin}${buildLoginSignupPath(
            nextPath,
            { login: isLoginMode, oauth: true }
          )}`
      const oauthOptions = {
        redirectTo,
        ...(provider === 'azure' ? { scopes: 'openid profile email' } : {}),
        queryParams: {
          prompt: 'select_account',
          ...(provider === 'google' ? { access_type: 'offline' } : {}),
        },
      }

      const { data: sessionData } = await supabase.auth.getSession()
      const currentUser = sessionData.session?.user
      // After logout the app immediately creates a new anonymous session.
      // Linking Google onto that anon fails with email_exists when the work
      // email already belongs to a real user — sign in instead so GoTrue
      // can attach to the existing account.
      const canLinkOntoCurrentUser = !!currentUser && !currentUser.is_anonymous

      if (canLinkOntoCurrentUser) {
        const { error: linkError } = await supabase.auth.linkIdentity({
          provider,
          options: oauthOptions,
        })
        if (!linkError) return
        if (
          !isIdentityAlreadyLinkedError(linkError) &&
          !isEmailAlreadyRegisteredError(linkError)
        ) {
          showError(t('Something went wrong', 'Något gick fel'), linkError.message)
          return
        }
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: oauthOptions,
      })
      if (error) {
        showError(t('Something went wrong', 'Något gick fel'), error.message)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('Unknown error occurred', 'Ett okänt fel uppstod')
      showError(t('Something went wrong', 'Något gick fel'), errorMessage)
    } finally {
      setOauthLoading(false)
    }
  }

  useEffect(() => {
    if (!isOAuthReturn || oauthReturnHandled.current) return

    const hashParams = new URLSearchParams(
      window.location.hash.replace(/^#/, '')
    )
    const errorDescription =
      searchParams.get('error_description') ||
      searchParams.get('error') ||
      hashParams.get('error_description') ||
      hashParams.get('error')
    if (errorDescription) {
      oauthReturnHandled.current = true
      showError(
        t('Something went wrong', 'Något gick fel'),
        errorDescription.replace(/\+/g, ' ')
      )
      clearOAuthSearchParams()
      return
    }

    if (authLoading) return

    const email = session?.user?.email?.trim()
    const user = session?.user
    const userId = user?.id
    if (!email || !userId || !user) {
      oauthReturnHandled.current = true
      showError(
        t('Something went wrong', 'Något gick fel'),
        t(
          'Could not complete sign in with that account. Please try again.',
          'Kunde inte slutföra inloggningen med det kontot. Försök igen.'
        )
      )
      clearOAuthSearchParams()
      return
    }

    const lockKey = oauthProvisionLockKey(userId)
    if (sessionStorage.getItem(lockKey)) {
      oauthReturnHandled.current = true
      clearOAuthSearchParams()
      navigate(nextPath)
      return
    }

    oauthReturnHandled.current = true
    sessionStorage.setItem(lockKey, '1')

    const finishOAuth = async () => {
      setOauthLoading(true)
      try {
        if (!isWorkEmail(email)) {
          showError(
            t('Work account required', 'Jobbkonto krävs'),
            t(
              'Please use a work Google or Microsoft account, not a personal email.',
              'Använd ett Google- eller Microsoft-jobbkonto, inte en personlig e-post.'
            )
          )
          await supabase.auth.signOut()
          const { error } = await supabase.auth.signInAnonymously()
          if (error) {
            console.error(
              'Failed to restore anonymous session after personal OAuth:',
              error
            )
          }
          sessionStorage.removeItem(lockKey)
          clearOAuthSearchParams()
          return
        }

        const orgUserResult = await queryClient.fetchQuery({
          queryKey: orgUserQueryKey(userId),
          queryFn: () => fetchOrgUserRow(supabase, userId),
        })
        const nestedOrg = orgUserResult.data?.organization
        const currentOrg = Array.isArray(nestedOrg) ? nestedOrg[0] : nestedOrg
        const existingOrgId = currentOrg?.id ?? orgUserResult.data?.organization_id
        const isExistingCustomer =
          !!existingOrgId && currentOrg?.name && currentOrg.name !== 'Anon'

        if (inviteOrganizationId) {
          const { firstName, lastName } = namesFromAuthMetadata(
            user.user_metadata,
            email
          )
          const profileId = orgUserResult.data?.user_id || userId
          const { error: profileError } = await supabase
            .from('user')
            .update({
              email,
              first_name: firstName,
              last_name: lastName || null,
            })
            .eq('id', profileId)
          if (profileError) {
            console.error('Failed to sync public.user after OAuth:', profileError)
          }
          clearOAuthSearchParams()
          await goToSignupSuccess(inviteOrganizationId)
          return
        }

        if (isExistingCustomer) {
          clearOAuthSearchParams()
          navigate(nextPath)
          return
        }

        const domain = extractDomainFromEmail(email)
        if (!domain) {
          showError(
            t('Invalid email', 'Ogiltig e-post'),
            t(
              'Could not extract domain from email address.',
              'Kunde inte extrahera domän från e-postadressen.'
            )
          )
          sessionStorage.removeItem(lockKey)
          clearOAuthSearchParams()
          return
        }

        const { firstName, lastName } = namesFromAuthMetadata(
          user.user_metadata,
          email
        )
        const cleanedUrl = cleanUrl(domain)
        const companyName = extractRootDomainName(domain)
        const selectedLogoUrl = generateBrandfetchUrls(cleanedUrl)[0] || null

        const profileId = orgUserResult.data?.user_id || userId
        const { error: profileError } = await supabase
          .from('user')
          .update({
            email,
            first_name: firstName,
            last_name: lastName || null,
          })
          .eq('id', profileId)
        if (profileError) {
          console.error('Failed to sync public.user after OAuth:', profileError)
        }

        let newOrgId: string
        if (existingOrgId) {
          newOrgId = existingOrgId
          const { error: orgError } = await supabase
            .from('organization')
            .update({
              name: companyName,
              logo_urls: selectedLogoUrl,
              company_url: cleanedUrl,
              chosen_logo_url: selectedLogoUrl,
              source: 'external',
            })
            .eq('id', newOrgId)
          if (orgError) throw orgError
        } else {
          // Creates the org and this user's membership in one step (RLS).
          const { data: createdOrgId, error: createError } = await supabase.rpc(
            'create_own_organization',
            {
              p_name: companyName,
              p_company_url: cleanedUrl,
              p_logo_urls: selectedLogoUrl ?? undefined,
              p_chosen_logo_url: selectedLogoUrl ?? undefined,
              p_source: 'external',
            }
          )
          if (createError) throw createError
          newOrgId = createdOrgId
        }

        await queryClient.invalidateQueries({
          queryKey: [queryKeys.organization],
        })
        await queryClient.invalidateQueries({ queryKey: [queryKeys.org_user] })
        await queryClient.invalidateQueries({ queryKey: [queryKeys.user] })

        trackEvent(supabase, {
          name: 'org_signup',
          orgId: newOrgId,
          onceKey: `org_signup:${newOrgId}`,
        })
        goToSignupSuccess(newOrgId)
      } catch (error) {
        sessionStorage.removeItem(lockKey)
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('Unknown error occurred', 'Ett okänt fel uppstod')
        showError(t('Something went wrong', 'Något gick fel'), errorMessage)
        clearOAuthSearchParams()
      } finally {
        setOauthLoading(false)
      }
    }

    finishOAuth()
  }, [
    isOAuthReturn,
    authLoading,
    session?.user?.email,
    session?.user?.id,
    searchParams,
  ])

  const sendMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}${nextPath}`,
      },
    })
    if (error) {
      showError(t('Something went wrong', 'Något gick fel'), error.message)
      return false
    }
    setSentToEmail(email)
    setMagicLinkSent(true)
    return true
  }

  const handleSubmit = async (
    data: AuthenticateFormData,
    imgUrls: string[]
  ) => {
    if (isLoginMode) {
      try {
        setLoading(true)
        await sendMagicLink(data.email)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('Unknown error occurred', 'Ett okänt fel uppstod')
        showError(t('Something went wrong', 'Något gick fel'), errorMessage)
      } finally {
        setLoading(false)
      }
      return
    }

    if (!inviteOrganizationId && imgUrls.length === 0) {
      showError(
        t('Invalid email', 'Ogiltig e-post'),
        t(
          'Please enter a valid work email address to load logos.',
          'Ange en giltig arbetsmejladress för att ladda logotyper.'
        )
      )
      return
    }

    try {
      setLoading(true)
      const domain = extractDomainFromEmail(data.email)

      if (!domain) {
        showError(
          t('Invalid email', 'Ogiltig e-post'),
          t(
            'Could not extract domain from email address.',
            'Kunde inte extrahera domän från e-postadressen.'
          )
        )
        return
      }

      const email = data.email.trim()
      const { error: authUpdateError } = await supabase.auth.updateUser({
        email,
      })
      if (authUpdateError) {
        if (isEmailAlreadyRegisteredError(authUpdateError)) {
          await sendMagicLink(email)
          return
        }
        showError(
          t('Something went wrong', 'Något gick fel'),
          authUpdateError.message
        )
        return
      }

      const emailParts = email.split('@')
      const firstNameRaw = emailParts[0] || ''
      const firstName =
        firstNameRaw.charAt(0).toUpperCase() +
        firstNameRaw.slice(1).toLowerCase()

      if (orgUser?.data?.user_id) {
        await updateUser.mutateAsync({
          id: orgUser.data.user_id,
          email,
          first_name: firstName,
        })
      }

      const { error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) {
        console.error('Failed to refresh session after signup:', refreshError)
      }

      if (inviteOrganizationId) {
        await goToSignupSuccess(inviteOrganizationId)
        return
      }

      const cleanedUrl = cleanUrl(domain)
      const companyName = extractRootDomainName(domain)
      const selectedLogoUrl = imgUrls[0]

      let newOrgId: string
      const existingOrgId = organization?.data?.id

      if (existingOrgId) {
        newOrgId = existingOrgId
        await updateOrganization.mutateAsync({
          id: newOrgId,
          name: companyName,
          logo_urls: selectedLogoUrl,
          company_url: cleanedUrl,
          chosen_logo_url: selectedLogoUrl,
          source: 'external',
        })
      } else {
        const res = await createOrganization2.mutateAsync({
          name: companyName,
          company_url: cleanedUrl,
          logo_urls: selectedLogoUrl,
          chosen_logo_url: selectedLogoUrl,
          source: 'external',
        })

        newOrgId = res.data?.id!
      }

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.organization],
      })

      if (newOrgId) {
        trackEvent(supabase, {
          name: 'org_signup',
          orgId: newOrgId,
          onceKey: `org_signup:${newOrgId}`,
        })
      }

      await goToSignupSuccess(newOrgId)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('Unknown error occurred', 'Ett okänt fel uppstod')
      showError(t('Something went wrong', 'Något gick fel'), errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return {
    handleSubmit,
    loading,
    oauthLoading,
    startOAuth,
    isOAuthReturn,
    magicLinkSent,
    sentToEmail,
    resetMagicLinkState,
  }
}
